import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { businessStore, ensurePinia, initStores } from "./store";
import { initUniversalLinks } from "@/utils/universal-link";
import { resetPageScroll } from "@/utils/page-scroll";
import { showAppSplash } from "@/utils/app-splash";
import { Capacitor } from "@capacitor/core";
import { installInlineSvgRuntime } from "@/utils/inline-svg";
import { i18n } from "@/i18n";

// Element Plus
import ElementPlus from "element-plus";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import "element-plus/dist/index.css";
import "element-plus/theme-chalk/dark/css-vars.css";

// TDesign
import TDesign from "tdesign-mobile-vue";
import "tdesign-mobile-vue/es/style/index.css";
import TDesignChat from "@tdesign-vue-next/chat";

// Ionic
import { IonicVue } from "@ionic/vue";
import { chevronBackOutline } from "ionicons/icons";
import { appNavAnimation } from "@/utils/nav-animation";
import { isMiniProgramWebview } from "@/utils/miniprogram-bridge";

// App styles
import "@/assets/css/app.css";
import "@/assets/css/app.less";

showAppSplash();

function getUrlParam(name) {
    if (typeof window === "undefined") return "";
    try {
        const searchValue = new URLSearchParams(window.location.search || "").get(name);
        if (searchValue) return searchValue;

        const hash = window.location.hash || "";
        const queryStart = hash.indexOf("?");
        if (queryStart === -1) return "";

        return new URLSearchParams(hash.slice(queryStart + 1)).get(name) || "";
    } catch (error) {
        return "";
    }
}

function setPixelCssVar(name, value) {
    if (typeof document === "undefined") return;
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue) || numberValue < 0) return;
    document.documentElement.style.setProperty(name, `${numberValue}px`);
}

const app = createApp(App)
    .use(IonicVue, {
        backButtonIcon: chevronBackOutline,
        navAnimation: appNavAnimation,
    })
    .use(ElementPlus)
    .use(TDesign)
    .use(TDesignChat)
    .use(businessStore)
    .use(i18n)
    .use(router);

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component);
}

// Store
const pinia = ensurePinia(app);
app.config.globalProperties.$resetPageScroll = resetPageScroll;

if (typeof document !== "undefined") {
    if (isMiniProgramWebview()) {
        document.documentElement.classList.add("v-miniprogram");
    }

    const platform = Capacitor?.isNativePlatform?.() ? Capacitor.getPlatform?.() || "" : "";
    if (platform) {
        document.documentElement.classList.add(`platform-${platform}`);
    }

    setPixelCssVar("--mp-status-bar-height", getUrlParam("mpStatus"));
    setPixelCssVar("--mp-menu-top", getUrlParam("mpMenuTop"));
    setPixelCssVar("--mp-menu-height", getUrlParam("mpMenuHeight"));
    setPixelCssVar("--mp-nav-height", getUrlParam("mpNav"));
    setPixelCssVar("--safe-area-inset-top", getUrlParam("mpStatus"));

    if (platform === "android") {
        document.documentElement.style.setProperty("--safe-area-inset-top", "24px");
        document.documentElement.style.setProperty("--safe-area-inset-bottom", "48px");
        document.documentElement.style.setProperty("--app-system-safe-area-bottom", "48px");
        document.documentElement.style.setProperty("--app-bottom-safe-fallback", "48px");
    }
}

router.isReady().then(() => {
    initStores(pinia).then(() => {
        initUniversalLinks(router);
        installInlineSvgRuntime(router);
        app.mount("#app");
    });
});
